import {SearchSchedulersQuery, useDeleteScheduler, useSearchSchedulers} from "../../client/hooks";
import {QueryClient, useMutation, useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";
import {Scheduler, SchedulerListe, SchedulerScope} from "../../api";
import {useNavigate} from "react-router-dom";
import {permissions} from "../../permissions";
import {useUserMandant} from "../useUserMandant";

export interface SchedulerFilter {
    search?: string;
}

export const useSchedulersHandling = () => {
    const {t: translation} = useTranslation('schedulers');
    const queryClient: QueryClient = useQueryClient();
    const navigate = useNavigate();
    const {isSystemUser, checkHasOneOfPermissions, checkMandantAndHasOneOfPermissions} = useUserMandant();

    const deleteScheduler: (schedulerId: string) => Promise<Scheduler> = useDeleteScheduler();
    const searchSchedulers: (query: SearchSchedulersQuery) => Promise<SchedulerListe> = useSearchSchedulers();

    const deleteSchedulerMutation = useMutation(
        (schedulerId: string): Promise<Scheduler> => deleteScheduler(schedulerId),
        {
            onSuccess: async (_data: Scheduler, schedulerId: string) => {
                await queryClient.invalidateQueries(["schedulers", schedulerId]);
                await queryClient.invalidateQueries(["schedulers"]);
                navigate("/schedulers");
            },
        }
    );

    const loadSchedulers = async (params: any, filter: SchedulerFilter): Promise<SchedulerListe> => {
        const search: string | undefined = filter.search?.toLowerCase();
        const filtered: string | undefined = params.filtered ?? undefined;
        const page: number = params.page ?? 0;
        const limit: number = params.limit ?? 25;
        const sort: string | undefined = params.sort ?? undefined;

        return await searchSchedulers({
            search: search,
            filtered: filtered,
            page: page,
            limit: limit,
            sort: sort,
        });
    }

    const loadSchedulersSelectableForAufgabe = async (mandantId?: string, aufgabeId?: string): Promise<SchedulerListe> => {
        return await searchSchedulers({
            mandantId: mandantId,
            selectableForAufgabe: aufgabeId,
            limit: 200,
            sort: "name",
        });
    }

    /** Like [loadSchedulers], but pinned to a fixed scope/owner (embedded tables on the Mandant/Aufgabe pages). */
    const loadSchedulersByOwner = async (
        params: any,
        filter: SchedulerFilter,
        fixed: { scope?: SchedulerScope; mandantId?: string; aufgabeId?: string },
    ): Promise<SchedulerListe> => {
        const search: string | undefined = filter.search?.toLowerCase();
        const filtered: string | undefined = params.filtered ?? undefined;
        const page: number = params.page ?? 0;
        const limit: number = params.limit ?? 25;
        const sort: string | undefined = params.sort ?? undefined;

        return await searchSchedulers({
            search: search,
            filtered: filtered,
            page: page,
            limit: limit,
            sort: sort,
            scope: fixed.scope,
            mandantId: fixed.mandantId,
            aufgabeId: fixed.aufgabeId,
        });
    }

    const canManageSchedulers = (): boolean => {
        return checkHasOneOfPermissions([
            permissions.schedulers.create,
            permissions.schedulers.edit,
            permissions.schedulers.delete,
            permissions.schedulers.own_create,
            permissions.schedulers.own_edit,
            permissions.schedulers.own_delete,
        ]);
    }

    const scopeLabel = (scope: SchedulerScope): string => translation(`scheduler-scope-${scope.toLowerCase()}`);

    /**
     * GLOBAL schedulers may only be managed by system users. MANDANT/AUFGABE schedulers may be
     * managed by system users or by users authorized for the scheduler's own Mandant.
     */
    const canEditScheduler = (scheduler: Scheduler): boolean => {
        if (scheduler.scope === "GLOBAL") {
            return isSystemUser;
        }
        return isSystemUser || checkMandantAndHasOneOfPermissions(scheduler.mandantId ?? "", [
            permissions.schedulers.edit,
            permissions.schedulers.own_edit,
        ]);
    }

    const canDeleteScheduler = (scheduler: Scheduler): boolean => {
        if (scheduler.scope === "GLOBAL") {
            return isSystemUser;
        }
        return isSystemUser || checkMandantAndHasOneOfPermissions(scheduler.mandantId ?? "", [
            permissions.schedulers.delete,
            permissions.schedulers.own_delete,
        ]);
    }

    return {
        translation,
        deleteSchedulerMutation,
        loadSchedulers,
        loadSchedulersSelectableForAufgabe,
        loadSchedulersByOwner,
        canManageSchedulers,
        canEditScheduler,
        canDeleteScheduler,
        scopeLabel,
    }
}
